import React, { useEffect, useState } from "react";
import { Sparkles, Gift, Building2, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ADS = [
  {
    eyebrow: "Limited offer",
    title: "Refer a friend, both pocket $25",
    body: "Send a FinVault invite. The day your friend funds their wallet, you both receive $25 — straight to your Live balance.",
    cta: "Open an account",
    href: "/register?promo=REFER25",
    Icon: Gift,
    bg: "from-[#1B3526] to-[#264C39]",
    accent: "text-[#B58957]",
    testId: "promo-referral",
  },
  {
    eyebrow: "Business",
    title: "First 90 days · zero monthly fee",
    body: "Open a FinVault Business account this quarter and waive your monthly platform fee for three full months. Payouts, transfers and card-linked deposits, all included.",
    cta: "Start a business account",
    href: "/register?promo=BIZ90",
    Icon: Building2,
    bg: "from-[#A85943] to-[#8A4632]",
    accent: "text-[#F0ECE0]",
    testId: "promo-business",
  },
  {
    eyebrow: "Try it risk-free",
    title: "+$1,000 Demo wallet on signup",
    body: "Sign up with code DEMO1000 and we'll add an extra $1,000 of simulated funds on top of the standard demo balance. Try every flow without spending a cent.",
    cta: "Claim the demo bonus",
    href: "/register?promo=DEMO1000",
    Icon: Sparkles,
    bg: "from-[#0A0A08] to-[#1B3526]",
    accent: "text-[#B58957]",
    testId: "promo-demo",
  },
  {
    eyebrow: "Security",
    title: "Login codes on every device",
    body: "Email OTP at every login, optional authenticator-app 2FA, and bcrypt-hashed passwords. Your peace of mind is the default setting.",
    cta: "How we protect you",
    href: "/about",
    Icon: ShieldCheck,
    bg: "from-[#264C39] to-[#1B3526]",
    accent: "text-[#B58957]",
    testId: "promo-security",
  },
];

export default function AuthPromo({ rotateMs = 6000 }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (rotateMs <= 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ADS.length), rotateMs);
    return () => clearInterval(t);
  }, [rotateMs]);

  const ad = ADS[idx];
  const Icon = ad.Icon;

  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="auth-promo">
      <div className={`absolute inset-0 bg-gradient-to-br ${ad.bg} transition-colors duration-700`} />
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: "radial-gradient(circle at 25px 25px, white 2px, transparent 0)",
        backgroundSize: "50px 50px",
      }} />
      <div className="relative h-full flex flex-col p-10 lg:p-14 text-white">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] opacity-80" data-testid="auth-promo-eyebrow">
          <span>{ad.eyebrow}</span>
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-md">
          <div className={`w-12 h-12 rounded-xl bg-white/10 ${ad.accent} flex items-center justify-center mb-6 backdrop-blur-sm`}>
            <Icon className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <h3 className="font-display text-3xl lg:text-4xl font-bold leading-tight" data-testid="auth-promo-title">{ad.title}</h3>
          <p className="mt-4 text-white/80 leading-relaxed" data-testid="auth-promo-body">{ad.body}</p>
          <Link
            to={ad.href}
            data-testid={`auth-promo-cta-${idx}`}
            className="mt-6 inline-flex items-center gap-2 self-start bg-white text-[#0A0A08] hover:bg-white/90 px-5 py-3 rounded-lg text-sm font-medium transition-colors"
          >
            {ad.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {/* Carousel indicators */}
        <div className="mt-8 flex items-center gap-2" data-testid="auth-promo-dots">
          {ADS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Show promotion ${i + 1}`}
              data-testid={`auth-promo-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
          <span className="ml-3 text-xs text-white/60">{idx + 1} / {ADS.length}</span>
        </div>
        <div className="absolute bottom-6 right-8 text-[10px] uppercase tracking-[0.24em] text-white/40">Sponsored · FinVault</div>
      </div>
    </div>
  );
}
